import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { SITE_URL } from "@/lib/seo";
import { searchIndex, getSearchIndex, type ScoredHit } from "@/lib/search-index";

/**
 * Internal search endpoint referenced by the WebSite > potentialAction
 * SearchAction in the root layout. Google uses this to render the
 * "Search box for site" sitelink directly inside the SERP.
 * URL shape: /search?q={search_term_string}
 */
const searchSchema = z.object({ q: z.string().max(200).optional().default("") });

export const Route = createFileRoute("/search")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => {
    const title = "Search DrHint — Live Rates, Bills & Tools";
    const desc = "Search across DrHint live gold rates, Pakistan utility bill checkers and developer APIs.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `${SITE_URL}/search` },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/search` }],
    };
  },
  component: SearchPage,
});

function SearchPage() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [draft, setDraft] = useState(q ?? "");

  const hits = useMemo<Array<ScoredHit | { title: string; path: string; group: string; icon: string; score?: number }>>(() => {
    const needle = (q ?? "").trim();
    if (!needle) {
      // Empty query — show a curated overview (first 30 of index)
      return getSearchIndex().slice(0, 30).map((h) => ({ ...h, score: 0 }));
    }
    return searchIndex(needle, 30);
  }, [q]);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-2">Search DrHint</h1>
        <p className="text-muted-foreground mb-6 text-sm">
          Live gold &amp; silver rates, utility bill checkers, calculators and developer APIs.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void navigate({ search: { q: draft } });
          }}
          className="flex gap-2"
        >
          <input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Try: gold rate in karachi, mepco bill, zakat calculator, prize bond…"
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            aria-label="Search DrHint"
          />
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Search
          </button>
        </form>

        <div className="mt-6 space-y-2">
          {hits.length === 0 ? (
            <div className="rounded-md border border-border bg-card/40 p-4 text-sm">
              <p className="mb-2 font-medium">No direct match for "{q}".</p>
              <p className="text-muted-foreground">Try single keywords like <b>karachi</b>, <b>mepco</b>, <b>zakat</b>, or <b>walmart</b>. You can also browse the <Link to="/calculators" className="text-primary underline">full calculator directory</Link>.</p>
            </div>
          ) : (
            hits.map((h) => (
              <Link
                key={h.path}
                to={h.path.split("#")[0].split("?")[0]}
                className="block rounded-md border border-border bg-card/40 px-4 py-3 hover:bg-muted/60"
              >
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span aria-hidden>{h.icon}</span>{h.title}
                </div>
                <div className="text-xs text-muted-foreground">{h.group} · {h.path}</div>
              </Link>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}